// Riwayat, the conversation history in the ChatShell sidebar (design review
// ui.region_gate side_history: rows, implement 0.81, relevance 2.7): the
// user's own conversations, newest first. Each row opens /asisten/:id and has
// a kebab Menu (ui/Menu.tsx) with Ganti nama (inline input in the row) and
// Hapus (a confirm Dialog naming the conversation). Search lives in the
// sidebar's Cari chat dialog. Project conversations stay out of Riwayat on
// purpose: they are listed on their project's page, and Cari chat searches
// them too (labelled with the project), so they are findable from anywhere.
import { useState, type ReactNode } from "react";
import * as API from "../api";
import type { ConversationSummary } from "../api";
import { SectionHead } from "../shell/SectionHead";
import { SideRow } from "../shell/SideRow";
import { NAV_HISTORY_KEY, useFlag } from "../lib/prefs";
import { Dialog, Notice, useAsync } from "../ui/components";

export interface HistorySectionProps {
  /** The open conversation (/asisten/:id), or null. */
  currentId: string | null;
  /** Bumped by the chat after every turn, so the list follows. */
  tick: number;
  onRenamed: (c: ConversationSummary) => void;
  onDeleted: (id: string) => void;
}

export function HistorySection({ currentId, tick, onRenamed, onDeleted }: HistorySectionProps) {
  const list = useAsync(() => API.listConversations(), [tick]);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConversationSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const rows = list.data ?? [];
  const [open, , toggle] = useFlag(NAV_HISTORY_KEY, true);
  // Folded, only the open conversation's row stays.
  const shown = open ? rows : rows.filter((c) => c.id === currentId);

  const rename = async (c: ConversationSummary, title: string) => {
    setBusy(true);
    setActionError(null);
    try {
      const next = await API.renameConversation(c.id, title);
      list.set(rows.map((r) => (r.id === c.id ? { ...r, ...next } : r)));
      onRenamed(next);
      setRenaming(null);
    } catch (err) {
      setActionError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm) return;
    setBusy(true);
    setActionError(null);
    try {
      await API.deleteConversation(confirm.id);
      list.set(rows.filter((r) => r.id !== confirm.id));
      onDeleted(confirm.id);
      setConfirm(null);
    } catch (err) {
      setActionError(API.errorMessage(err));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  let body: ReactNode;
  if (list.loading && !list.data) {
    body = (
      <p className="cs-note" role="status">
        Memuat riwayat
      </p>
    );
  } else if (list.error) {
    body = (
      <div className="cs-note">
        <span>Riwayat belum termuat.</span>
        <button type="button" className="btn btn-secondary" onClick={list.reload}>
          Coba lagi
        </button>
      </div>
    );
  } else if (!open && shown.length === 0) {
    body = null;
  } else if (rows.length === 0) {
    body = <p className="cs-note">Belum ada percakapan. Pertanyaan pertama Anda akan tersimpan di sini.</p>;
  } else {
    body = (
      <ul className="cs-rows" aria-labelledby="cs-history-title">
        {shown.map((c) => (
          <SideRow
            key={c.id}
            label={c.title}
            href={`/asisten/${encodeURIComponent(c.id)}`}
            current={c.id === currentId}
            menuLabel={`Tindakan untuk ${c.title}`}
            items={[
              { id: "rename", label: "Ganti nama", icon: "edit2", onSelect: () => setRenaming(c.id) },
              { id: "delete", label: "Hapus", icon: "trash", tone: "danger", onSelect: () => setConfirm(c) },
            ]}
            renaming={renaming === c.id}
            busy={busy}
            inputLabel="Nama percakapan"
            maxLength={200}
            onRename={(title) => rename(c, title)}
            onCancelRename={() => setRenaming(null)}
          />
        ))}
      </ul>
    );
  }

  return (
    <section className="cs-section" aria-labelledby="cs-history-title">
      <SectionHead id="cs-history-title" title="Riwayat" open={open} onToggle={toggle} controls="cs-history-list" />
      {actionError ? (
        <Notice tone="danger" title="Tindakan belum berhasil" className="cs-notice">
          <p>{actionError}</p>
        </Notice>
      ) : null}
      <div id="cs-history-list" className="cs-fold" data-folded={open ? undefined : ""}>
        {body}
      </div>
      <Dialog
        open={confirm !== null}
        title={confirm ? `Hapus percakapan “${confirm.title}”?` : "Hapus percakapan?"}
        size="sm"
        busy={busy}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirm(null)} disabled={busy}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" data-tone="danger" onClick={remove} aria-busy={busy || undefined} disabled={busy}>
              Hapus
            </button>
          </>
        }
      >
        <p className="dialog-text">Percakapan ini dan semua pesannya akan dihapus permanen dari riwayat Anda. Tindakan ini tidak dapat dibatalkan.</p>
      </Dialog>
    </section>
  );
}
